/**
 * Enemy definitions — scaled lightly by stage when spawning.
 */

export enum EnemyKind {
  Crawler = 'crawler',
  Grub = 'grub',
  Bat = 'bat',
  VaultSentinel = 'vault_sentinel',
  SpireMother = 'spire_mother',
  SpireTotem = 'spire_totem',
  TunnelHorror = 'tunnel_horror',
}

export interface EnemyDef {
  kind: EnemyKind;
  name: string;
  maxHealth: number;
  /** Contact damage dealt to the player. */
  damage: number;
  /** Horizontal chase speed (world units / sec). */
  speed: number;
  /** Distance at which the enemy starts chasing. */
  aggroRange: number;
  /** Seconds between contact hits. */
  attackCooldown: number;
  halfExtents: { x: number; y: number; z: number };
  /** Body color (hex). */
  color: number;
  /** Soft glow. */
  emissive: number;
}

export const ENEMY_CATALOG: Record<EnemyKind, EnemyDef> = {
  [EnemyKind.Crawler]: {
    kind: EnemyKind.Crawler,
    name: 'Cave Crawler',
    maxHealth: 28,
    damage: 14,
    speed: 3.4,
    aggroRange: 11,
    attackCooldown: 0.7,
    halfExtents: { x: 0.4, y: 0.28, z: 0.42 },
    color: 0xa85a40,
    emissive: 0x5a2818,
  },
  [EnemyKind.Grub]: {
    kind: EnemyKind.Grub,
    name: 'Rock Grub',
    maxHealth: 55,
    damage: 22,
    speed: 2.1,
    aggroRange: 9,
    attackCooldown: 0.9,
    halfExtents: { x: 0.48, y: 0.4, z: 0.58 },
    color: 0x8aaa68,
    emissive: 0x3a4a22,
  },
  [EnemyKind.Bat]: {
    kind: EnemyKind.Bat,
    name: 'Glow Bat',
    maxHealth: 18,
    damage: 12,
    speed: 4.6,
    aggroRange: 13,
    attackCooldown: 0.6,
    halfExtents: { x: 0.34, y: 0.24, z: 0.3 },
    color: 0x6a5aaa,
    emissive: 0xaa55ee,
  },
  [EnemyKind.VaultSentinel]: {
    kind: EnemyKind.VaultSentinel,
    name: 'Vault Sentinel',
    maxHealth: 320,
    damage: 22,
    speed: 2.4,
    aggroRange: 28,
    attackCooldown: 0.7,
    halfExtents: { x: 0.9, y: 1.15, z: 0.8 },
    color: 0x5a6474,
    emissive: 0x304060,
  },
  [EnemyKind.SpireMother]: {
    kind: EnemyKind.SpireMother,
    name: 'Spire Mother',
    maxHealth: 280,
    damage: 18,
    speed: 2.8,
    aggroRange: 30,
    attackCooldown: 0.65,
    halfExtents: { x: 0.75, y: 1.0, z: 0.7 },
    color: 0x5a3a7a,
    emissive: 0x8822aa,
  },
  [EnemyKind.SpireTotem]: {
    kind: EnemyKind.SpireTotem,
    name: 'Spire Totem',
    maxHealth: 70,
    damage: 0,
    speed: 0,
    aggroRange: 0,
    attackCooldown: 99,
    halfExtents: { x: 0.42, y: 0.9, z: 0.42 },
    color: 0x3a2a55,
    emissive: 0xcc44ff,
  },
  [EnemyKind.TunnelHorror]: {
    kind: EnemyKind.TunnelHorror,
    name: 'Tunnel Horror',
    maxHealth: 300,
    damage: 28,
    speed: 3.2,
    aggroRange: 32,
    attackCooldown: 0.8,
    halfExtents: { x: 0.78, y: 0.58, z: 1.15 },
    color: 0x4a3020,
    emissive: 0xaa3310,
  },
};

/** Boss stages land every 5 endless stages (5, 10, 15, …). */
export function isBossStage(stage: number): boolean {
  return stage > 0 && stage % 5 === 0;
}

const BOSS_ROTATION: readonly EnemyKind[] = [
  EnemyKind.VaultSentinel,
  EnemyKind.SpireMother,
  EnemyKind.TunnelHorror,
];

/** Rotate the three bosses across boss stages. */
export function bossKindForStage(stage: number): EnemyKind {
  if (!isBossStage(stage)) return EnemyKind.VaultSentinel;
  const index = Math.floor(stage / 5 - 1) % BOSS_ROTATION.length;
  return BOSS_ROTATION[index]!;
}

export function isBossKind(kind: EnemyKind): boolean {
  return (
    kind === EnemyKind.VaultSentinel ||
    kind === EnemyKind.SpireMother ||
    kind === EnemyKind.TunnelHorror
  );
}

/** Entities that must die before a boss stage exit unlocks. */
export function isBossClearTarget(kind: EnemyKind): boolean {
  return isBossKind(kind) || kind === EnemyKind.SpireTotem;
}

/** How many enemies to place for a given endless stage number. */
export function enemyCountForStage(stage: number): number {
  return Math.min(20, 5 + Math.floor(stage * 1.6));
}

/** Pick an enemy kind weighted toward tougher types deeper down. */
export function pickEnemyKind(stage: number, roll: number): EnemyKind {
  const t = Math.min(1, (stage - 1) / 8);
  if (roll < 0.35 - t * 0.1) return EnemyKind.Crawler;
  if (roll < 0.7 - t * 0.05) return EnemyKind.Grub;
  return EnemyKind.Bat;
}

/** Mild HP / damage scaling per stage. */
export function stageScale(stage: number): { hp: number; damage: number } {
  const n = Math.max(0, stage - 1);
  return {
    hp: 1 + n * 0.12,
    damage: 1 + n * 0.08,
  };
}
